import React, { useEffect, useState } from 'react';
import { shouldPromptInstall, markInstallPrompted } from '../utils/fullscreen';
import './styles/IOSInstallPrompt.css';

interface IOSInstallPromptProps {
  onDismiss?: () => void;
}

/**
 * Prompts iOS users to "Add to Home Screen" for fullscreen experience
 * Only shows on iOS devices when not already in standalone mode
 */
export const IOSInstallPrompt: React.FC<IOSInstallPromptProps> = ({
  onDismiss,
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    // Detect Safari
    const userAgent = navigator.userAgent.toLowerCase();
    const safari =
      /safari/.test(userAgent) && !/chrome|crios|fxios/.test(userAgent);
    setIsSafari(safari);

    // Check if we should show the prompt
    if (shouldPromptInstall()) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setShowPrompt(false);

    // Only mark as prompted if user checked "don't show again"
    if (dontShowAgain) {
      markInstallPrompted();
    }

    onDismiss?.();
  };

  if (!showPrompt) return null;

  return (
    <div className="ios-install-overlay">
      <div className="ios-install-prompt">
        <button className="ios-install-close" onClick={handleDismiss}>
          ×
        </button>
        <div className="ios-install-content">
          <h3>Install for Fullscreen Experience</h3>
          <p>
            <strong>Note:</strong> Browsers always show the address bar. To use
            this app in fullscreen mode, install it to your home screen:
          </p>
          {isSafari ? (
            <ol>
              <li>
                Tap the <strong>Share</strong> button{' '}
                <span className="ios-share-icon">
                  <svg
                    width="16"
                    height="20"
                    viewBox="0 0 16 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8 0L8 13M8 0L4 4M8 0L12 4M1 14V18C1 19.1046 1.89543 20 3 20H13C14.1046 20 15 19.1046 15 18V14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>{' '}
                in the Safari toolbar
              </li>
              <li>
                Scroll and tap <strong>"Add to Home Screen"</strong>
              </li>
              <li>
                <strong>Launch the app from your home screen</strong>
              </li>
            </ol>
          ) : (
            <ol>
              <li>
                Tap the <strong>menu</strong> button (⋮ or ⋯) in your browser
              </li>
              <li>
                Select <strong>"Add to Home Screen"</strong> or{' '}
                <strong>"Install App"</strong>
              </li>
              <li>
                <strong>Launch the app from your home screen</strong>
              </li>
            </ol>
          )}
          <p style={{ marginTop: '16px', fontSize: '14px', color: '#888' }}>
            Once installed, the app will open in fullscreen for a cleaner
            experience.
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
            padding: '0 4px',
          }}
        >
          <input
            type="checkbox"
            id="dont-show-again"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            style={{
              width: '18px',
              height: '18px',
              cursor: 'pointer',
              accentColor: '#2a9d8f',
            }}
          />
          <label
            htmlFor="dont-show-again"
            style={{
              fontSize: '14px',
              color: '#666',
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            Don't show this message again
          </label>
        </div>
        <button className="ios-install-dismiss" onClick={handleDismiss}>
          Got it
        </button>
      </div>
    </div>
  );
};

export default IOSInstallPrompt;
